class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.toggleAddButton(true, '', false);

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.dispatchVariantChange();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = document.querySelector(`[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`);
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => {
      parent.scroll(0, 0);
    });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('hidden');
        if (this.currentVariant) {
          this.toggleAddButton(!this.currentVariant.available, window.variantStrings.soldOut);
        }

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document.querySelector(`#product-form-${this.dataset.section}`).querySelector('input[name="inventory_quantity"]');
        const inventoryQtySource = html.querySelector(`#product-form-${this.dataset.section}`).querySelector('input[name="inventory_quantity"]');
        inventoryQtyDestination.value = inventoryQtySource.value;
      });
  }

  /**
   * toggleAddButton
   * @param {Boolean} disable
   * @param {String} text
   * @param {Boolean} modifyClass
   * @returns {void}
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]');
    const addButtonText = addButton.querySelector('[data-add-to-cart-button-text]');

    if (!addButton || !addButtonText) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const addButton = document.getElementById(`product-form-${this.dataset.section}`)?.querySelector('[name="add"]');
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get('variant-selects')) {
  customElements.define('variant-selects', VariantSelects);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
    });
  }
}

if (!customElements.get('variant-radios')) {
  customElements.define('variant-radios', VariantRadios);
}

/**
 * VariantButtons - Enhanced variant selector with availability-aware button states
 * Uses the same button-based UI as the pillow configurator.
 *
 * Features:
 * - Hides option values that don't exist for the current option combination
 * - Greys out option values that exist but are unavailable (out of stock)
 * - Uses "prior options" logic to avoid circular dependencies
 * - Uses .active class for selected state (like pillow configurator)
 */
class VariantButtons extends VariantSelects {
  constructor() {
    super();
    this.hideNonExistent = true;
    this.greyOutUnavailable = true;
    this._isUpdating = false;
  }

  connectedCallback() {
    this.attachButtonListeners();
    this.updateButtonAvailability();
    this.updateOptions();
    this.updateMasterId();
  }

  attachButtonListeners() {
    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.configurator-attribute-button');
      if (!btn || btn.disabled) return;

      e.preventDefault();
      this.handleButtonClick(btn);
    });
  }

  /**
   * Handle button click - update active state and trigger variant change
   */
  handleButtonClick(btn) {
    if (this._isUpdating) return;

    const group = btn.closest('.configurator-attribute-group');
    if (!group) return;

    // Remove active from siblings, add to clicked
    group.querySelectorAll('.configurator-attribute-button.active').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');

    // Trigger variant change
    this.onVariantChange();
  }

  /**
   * Override updateOptions to read from active buttons instead of inputs
   */
  updateOptions() {
    const groups = Array.from(this.querySelectorAll('.configurator-attribute-group'));
    this.options = groups.map((group) => {
      const activeBtn = group.querySelector('.configurator-attribute-button.active');
      return activeBtn ? activeBtn.dataset.value : '';
    });
  }

  onVariantChange() {
    if (this._isUpdating) return;
    this._isUpdating = true;

    try {
      // Read current options from active buttons
      this.updateOptions();
      this.updateMasterId();

      // Update button visibility/availability states
      this.updateButtonAvailability();

      // Re-read options in case active buttons changed due to availability
      this.updateOptions();
      this.updateMasterId();

      this.toggleAddButton(true, '', false);

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        this.updateURL();
        this.updateVariantInput();
        this.renderProductInfo();
        this.dispatchVariantChange();
      }
    } finally {
      this._isUpdating = false;
    }
  }

  /**
   * Get currently selected options up to (but not including) a given index
   * This prevents circular dependencies where later options affect earlier ones
   */
  getPriorOptions(upToIndex) {
    const groups = Array.from(this.querySelectorAll('.configurator-attribute-group'));
    const priorOptions = [];

    for (let i = 0; i < upToIndex && i < groups.length; i++) {
      const activeBtn = groups[i].querySelector('.configurator-attribute-button.active');
      if (activeBtn) {
        priorOptions.push(activeBtn.dataset.value);
      }
    }

    return priorOptions;
  }

  /**
   * Check if an option value exists and is available given prior selections
   *
   * @param {string} optionValue - The option value to check
   * @param {number} optionIndex - The index of the option group (0-based)
   * @returns {{ exists: boolean, available: boolean }}
   */
  checkOptionAvailability(optionValue, optionIndex) {
    const variants = this.getVariantData();
    const priorOptions = this.getPriorOptions(optionIndex);

    let exists = false;
    let available = false;

    for (const variant of variants) {
      // Check if variant matches all prior options
      let matchesPrior = true;
      for (let i = 0; i < priorOptions.length; i++) {
        if (variant.options[i] !== priorOptions[i]) {
          matchesPrior = false;
          break;
        }
      }

      if (!matchesPrior) continue;

      // Check if this variant has the option value we're testing
      if (variant.options[optionIndex] === optionValue) {
        exists = true;
        if (variant.available) {
          available = true;
          break; // Found an available variant, no need to continue
        }
      }
    }

    return { exists, available };
  }

  /**
   * Update all button states based on availability
   * - Hide non-existent options (display: none)
   * - Grey out unavailable options (opacity: 0.5)
   * - Ensure active button stays visible
   */
  updateButtonAvailability() {
    const groups = Array.from(this.querySelectorAll('.configurator-attribute-group'));

    groups.forEach((group, optionIndex) => {
      const buttons = group.querySelectorAll('.configurator-attribute-button');
      let hasVisibleActive = false;
      let firstVisibleButton = null;
      let firstAvailableButton = null;

      buttons.forEach((btn) => {
        const value = btn.dataset.value;
        const { exists, available } = this.checkOptionAvailability(value, optionIndex);

        // Store availability data on the element
        btn.dataset.exists = exists;
        btn.dataset.available = available;

        // Determine visibility
        // First option group (index 0) always shows all options
        const shouldHide = this.hideNonExistent && optionIndex > 0 && !exists;

        btn.style.display = shouldHide ? 'none' : '';

        // Determine opacity for greying out (like pillow configurator)
        if (this.greyOutUnavailable && !available && !shouldHide) {
          btn.style.opacity = '0.5';
        } else {
          btn.style.opacity = '';
        }

        // Track visible buttons
        if (!shouldHide) {
          if (!firstVisibleButton) firstVisibleButton = btn;
          if (available && !firstAvailableButton) firstAvailableButton = btn;
          if (btn.classList.contains('active')) hasVisibleActive = true;
        }
      });

      // If active button is hidden, select a new one
      if (!hasVisibleActive && (firstAvailableButton || firstVisibleButton)) {
        const newSelection = firstAvailableButton || firstVisibleButton;
        // Remove active from any hidden button
        group.querySelectorAll('.configurator-attribute-button.active').forEach((b) => b.classList.remove('active'));
        newSelection.classList.add('active');
      }
    });
  }
}

if (!customElements.get('variant-buttons')) {
  customElements.define('variant-buttons', VariantButtons);
}
